import React, { useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import CustomTextField from "../../../../../components/customMUIComponents/CustomTextField";
import Typography from "@mui/material/Typography";
import { Button, Chip, Grid2, MenuItem } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

const SECTOR_KEY_TO_TEXT_MAP = new Map<string, string>([
    ["SIZE", "Taille"],
    ["COLOR", "Couleur"],
    ["INSCRIPTION", "Inscription"],
    ["CUSTOMIZED", "Personnalisé"],
]);

const DEFAULT_OPTIONS_MAP = new Map<string, string[]>([
    ["COLOR", ["bleu", "vert", "rouge"]],
    ["SIZE", ["xs", "s", "m", "l", "xl", "xxl", "xxxl"]],
]);

interface Variant {
    id: number;
    type: string;
    title: string;
    options: string[];
}

const ArticleVariantsDefinition = () => {
    const [variants, setVariants] = useState<Variant[]>([]);
    const [nextVariantId, setNextVariantId] = useState<number>(0);

    const [editingOption, setEditingOption] = useState<{ variantId: number; oldOptionValue: string } | null>(null);
    const [editedOptionText, setEditedOptionText] = useState<string>('');

    // --- New: Memoized list of available variant types for dropdowns ---
    const availableVariantTypes = useMemo(() => {
        const selectedTypes = new Set(variants.map(v => v.type));
        return Array.from(SECTOR_KEY_TO_TEXT_MAP.entries()).filter(([key]) => !selectedTypes.has(key));
    }, [variants]);

    // --- New: Check if all variant types are selected ---
    const allTypesSelected = variants.length === SECTOR_KEY_TO_TEXT_MAP.size;

    const handleAddVariant = useCallback(() => {
        // Prevent adding if all types are already selected
        if (allTypesSelected) {
            return;
        }

        // Find the first available type that hasn't been selected yet
        const firstAvailableType = Array.from(SECTOR_KEY_TO_TEXT_MAP.keys()).find(
            (typeKey) => !variants.some(v => v.type === typeKey)
        );

        if (firstAvailableType) {
            const initialTitle = SECTOR_KEY_TO_TEXT_MAP.get(firstAvailableType) || '';
            const defaultOptionsForNewVariant = DEFAULT_OPTIONS_MAP.get(firstAvailableType) || [];

            setVariants((prevVariants) => [
                ...prevVariants,
                {
                    id: nextVariantId,
                    type: firstAvailableType,
                    title: initialTitle,
                    options: defaultOptionsForNewVariant,
                },
            ]);
            setNextVariantId((prevId) => prevId + 1);
        }
    }, [nextVariantId, variants, allTypesSelected]); // Added variants and allTypesSelected to dependencies

    const handleVariantTypeChange = useCallback(
        (id: number, newType: string) => {
            setVariants((prevVariants) =>
                prevVariants.map((variant) => {
                    // If this variant is already of the newType, or if the newType is already selected by another variant, do nothing
                    if (variant.id !== id && variant.type === newType) {
                        return variant; // Don't allow changing to an already selected type
                    }
                    if (variant.id === id) {
                        const newTitle = newType !== 'CUSTOMIZED' ? SECTOR_KEY_TO_TEXT_MAP.get(newType) || '' : variant.title;
                        const defaultOptions = DEFAULT_OPTIONS_MAP.get(newType) || [];
                        return {
                            ...variant,
                            type: newType,
                            title: newTitle,
                            options: defaultOptions
                        };
                    }
                    return variant;
                })
            );
        },
        [setVariants]
    );

    const handleVariantTitleChange = useCallback(
        (id: number, newTitle: string) => {
            setVariants((prevVariants) =>
                prevVariants.map((variant) =>
                    variant.id === id ? { ...variant, title: newTitle } : variant
                )
            );
        },
        [setVariants]
    );

    const handleAddOption = useCallback(
        (id: number) => {
            setVariants((prevVariants) =>
                prevVariants.map((variant) =>
                    variant.id === id ? { ...variant, options: [...variant.options, 'Nouvelle option'] } : variant
                )
            );
        },
        [setVariants]
    );

    const handleDeleteOption = useCallback(
        (variantId: number, optionToDelete: string) => {
            setVariants((prevVariants) =>
                prevVariants.map((variant) =>
                    variant.id === variantId
                        ? { ...variant, options: variant.options.filter((option) => option !== optionToDelete) }
                        : variant
                )
            );
        },
        [setVariants]
    );

    const handleDeleteVariant = useCallback(
        (id: number) => {
            setVariants((prevVariants) => prevVariants.filter((variant) => variant.id !== id));
        },
        [setVariants]
    );

    const handleEditIconClick = useCallback((variantId: number, optionValue: string) => {
        setEditingOption({ variantId, oldOptionValue: optionValue });
        setEditedOptionText(optionValue);
    }, []);

    const handleOptionInputChange = useCallback((value: string) => {
        setEditedOptionText(value);
    }, []);

    const handleOptionInputBlur = useCallback(() => {
        if (editingOption) {
            const { variantId, oldOptionValue } = editingOption;
            setVariants((prevVariants) =>
                prevVariants.map((variant) =>
                    variant.id === variantId
                        ? {
                            ...variant,
                            options: variant.options.map((opt) =>
                                opt === oldOptionValue ? editedOptionText : opt
                            ),
                        }
                        : variant
                )
            );
            setEditingOption(null);
            setEditedOptionText('');
        }
    }, [editingOption, editedOptionText, setVariants]);

    const handleOptionInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
        }
    }, []);

    return (
        <Box>
            <Typography variant={"firstVariant"} textAlign={"justify"} mb={2}>
                Les variantes d'articles peuvent être utiles si tu proposes un même article en différentes tailles,
                couleurs ou si tu proposes à l'utilisateur de le personnaliser en y gravant son prénom par exemple. Si
                ce n'est pas le cas pour cet article, tu peux ignorer cette étape.
            </Typography>

            <Typography variant={"firstVariant"} textAlign={"justify"} mb={2}>
                Nous proposons quelques options par défaut mais tu peux également créer les tiennes. Tu peux bien
                entendu ajouter plusieurs variantes. Exemple: un habit pour lequel l'utilisateur peut choisir la taille et la
                couleur.
            </Typography>

            {variants.map((variant) => (
                <Grid2 key={variant.id} container size={12} mt={3} mb={3}
                       sx={{ border: '1px solid #ccc', padding: 2, borderRadius: 1 }}>
                    <Grid2 size={12} container spacing={2}>
                        <Grid2 size={5} container>
                            <CustomTextField
                                variant={"outlined"}
                                value={variant.type}
                                label={"Type de variante"}
                                setField={(newType) => handleVariantTypeChange(variant.id, newType)}
                                select
                                selectOptions={Array.from(SECTOR_KEY_TO_TEXT_MAP.entries()).map(([key, value]) => {
                                    const isSelected = variants.some(v => v.type === key && v.id !== variant.id);
                                    return (
                                        <MenuItem key={key} value={key} disabled={isSelected}>
                                            {value}
                                        </MenuItem>
                                    );
                                })}
                                mb={2}
                                textAlign={"left"}
                                required
                            />
                        </Grid2>
                        {variant.type === 'CUSTOMIZED' && (
                            <Grid2 size={5} container>
                                <CustomTextField
                                    variant={"outlined"}
                                    value={variant.title}
                                    setField={(newTitle) => handleVariantTitleChange(variant.id, newTitle)}
                                    label="Titre de la variante"
                                    required
                                    disabled={variant.type !== 'CUSTOMIZED'}
                                />
                            </Grid2>
                        )}
                    </Grid2>
                    <Grid2 size={12} container spacing={1} mt={2} alignItems="center">
                        {variant.options.map((option) => (
                            <React.Fragment key={option}>
                                {editingOption && editingOption.variantId === variant.id && editingOption.oldOptionValue === option ? (
                                    <CustomTextField
                                        value={editedOptionText}
                                        setField={handleOptionInputChange}
                                        onBlur={handleOptionInputBlur}
                                        onKeyDown={handleOptionInputKeyDown}
                                        autoFocus
                                        variant="outlined"
                                        label="Modifier l'option"
                                        fullWidth={false}
                                    />
                                ) : (
                                    <Chip
                                        label={option}
                                        onDelete={() => handleDeleteOption(variant.id, option)}
                                        icon={<EditIcon />}
                                        onClick={() => handleEditIconClick(variant.id, option)}
                                        sx={{ mr: 1, mb: 1 }}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                        <Button size="small" onClick={() => handleAddOption(variant.id)} sx={{ ml: 1 }}>
                            Ajouter une option
                        </Button>
                    </Grid2>
                    <Grid2 size={12} container spacing={2} mt={2}>
                        <Button size="small" onClick={() => handleDeleteVariant(variant.id)} sx={{ ml: 1 }} color="error">
                            Supprimer la variante
                        </Button>
                    </Grid2>
                </Grid2>
            ))}

            <Button
                onClick={handleAddVariant}
                variant="contained"
                color="primary"
                disabled={allTypesSelected} // <-- Disable button if all types are selected
            >
                Ajouter une variante
            </Button>
        </Box>
    );
};

export default ArticleVariantsDefinition;
